import { useService } from '@toeverything/infra';
import { useEffect, useRef } from 'react';

import type { PageContext } from '../types';
import { PerformanceMonitorService } from '../services/performance-monitor';

/**
 * 监控页面加载性能
 * @param pageContext 页面上下文（包含页面类型、主客态等）
 * @param isReady 页面是否加载完成
 */
export function usePagePerformance(
  pageContext: PageContext,
  isReady: boolean
) {
  const perfMonitor = useService(PerformanceMonitorService);
  const hasInitialized = useRef(false);
  const hasReported = useRef(false);

  useEffect(() => {
    perfMonitor.setPageContext(pageContext);
    perfMonitor.mark('page:load:start');
    hasInitialized.current = true;
    hasReported.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perfMonitor, pageContext.pageType]);

  useEffect(() => {
    if (isReady && !hasReported.current) {
      perfMonitor.mark('page:load:complete');
      const duration = perfMonitor.measure(
        'page:totalLoadTime',
        'page:load:start',
        'page:load:complete'
      );
      perfMonitor.setPageLoadStage('renderComplete', duration);

      // 延迟3秒上报，等待Web Vitals收集完成
      setTimeout(() => {
        perfMonitor.report().catch(() => {
          // 静默失败
        });
        hasReported.current = true;
      }, 3000);
    }
  }, [isReady, perfMonitor]);

  return perfMonitor;
}

/**
 * 监控文档页面加载各阶段
 */
export function useDocPagePerformance(
  pageContext: PageContext,
  stages: {
    docListReady?: boolean;
    docOpened?: boolean;
    editorCreated?: boolean;
    dataReady?: boolean;
  }
) {
  const perfMonitor = useService(PerformanceMonitorService);
  const stageMarked = useRef({
    docListReady: false,
    docOpened: false,
    editorCreated: false,
    dataReady: false,
  });

  useEffect(() => {
    perfMonitor.setPageContext(pageContext);
    perfMonitor.mark('doc:load:start');
    
    // 重置阶段标记
    stageMarked.current = {
      docListReady: false,
      docOpened: false,
      editorCreated: false,
      dataReady: false,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perfMonitor, pageContext.docId]);

  useEffect(() => {
    if (stages.docListReady && !stageMarked.current.docListReady) {
      perfMonitor.mark('doc:list:ready');
      const duration = perfMonitor.measure(
        'doc:listReadyTime',
        'doc:load:start',
        'doc:list:ready'
      );
      perfMonitor.setPageLoadStage('docListReady', duration);
      stageMarked.current.docListReady = true;
    }
  }, [stages.docListReady, perfMonitor]);

  useEffect(() => {
    if (stages.docOpened && !stageMarked.current.docOpened) {
      perfMonitor.mark('doc:opened');
      const duration = perfMonitor.measure(
        'doc:openTime',
        'doc:load:start',
        'doc:opened'
      );
      perfMonitor.setPageLoadStage('docOpened', duration);
      stageMarked.current.docOpened = true;
    }
  }, [stages.docOpened, perfMonitor]);

  useEffect(() => {
    if (stages.editorCreated && !stageMarked.current.editorCreated) {
      perfMonitor.mark('doc:editor:created');
      const duration = perfMonitor.measure(
        'doc:editorCreateTime',
        'doc:load:start',
        'doc:editor:created'
      );
      perfMonitor.setPageLoadStage('editorCreated', duration);
      stageMarked.current.editorCreated = true;
    }
  }, [stages.editorCreated, perfMonitor]);

  useEffect(() => {
    if (stages.dataReady && !stageMarked.current.dataReady) {
      perfMonitor.mark('doc:data:ready');
      const duration = perfMonitor.measure(
        'doc:dataReadyTime',
        'doc:load:start',
        'doc:data:ready'
      );
      perfMonitor.setPageLoadStage('dataReady', duration);
      stageMarked.current.dataReady = true;

      // 延迟3秒上报，等待Web Vitals收集完成
      setTimeout(() => {
        perfMonitor.report().catch(() => {
          // 静默失败
        });
      }, 3000);
    }
  }, [stages.dataReady, perfMonitor]);

  return perfMonitor;
}
